/**
 * Field Mapping Utilities
 * Centralized mapping functions to normalize inconsistent field names between DB and UI
 * 
 * @example
 * // DB uses snake_case: net_import
 * // UI uses Title Case: Net Import
 * const uiValue = normalizeNetImport(dbValue);
 * const dbValue = denormalizeNetImport(uiValue);
 */

// ==================== NET IMPORT MAPPER ====================
/**
 * Normalize: DB net_import field → UI "Net Import" field
 * Handles empty values and maintains data type consistency
 * @param value - Raw value from database (string or number)
 * @returns String representation for UI display
 */
export const normalizeNetImport = (value: any): string => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value.trim();
  return '';
};

/**
 * Denormalize: UI "Net Import" field → DB net_import field
 * Converts UI input back to database format
 * @param value - Value from UI input
 * @returns Numeric value for database or null if empty
 */
export const denormalizeNetImport = (value: any): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = parseFloat(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
};

// ==================== FEEDER MAPPER ====================
/**
 * Map backend feeder field names to UI column headers
 * Handles all feeder-related fields with consistent naming
 */
export const feederFieldMapping = {
  // Feeder 1 mappings
  'feeder1_initial_export': 'Feeder1 Initial Export',
  'feeder1_final_export': 'Feeder1 Final Export',
  'feeder1_net_kwh': 'Feeder1 Net kWh',
  'feeder1_initial_import_kwh': 'Feeder1 Initial import kWh',
  'feeder1_final_import_kwh': 'Feeder1 Final import kWh',

  // Inverter mappings
  'inverter_generation_600v': 'Inverter Generation 600V',

  // Reverse mapping (UI to DB)
  'Feeder1 Initial Export': 'feeder1_initial_export',
  'Feeder1 Final Export': 'feeder1_final_export',
  'Feeder1 Net kWh': 'feeder1_net_kwh',
  'Feeder1 Initial import kWh': 'feeder1_initial_import_kwh',
  'Feeder1 Final import kWh': 'feeder1_final_import_kwh',
  'Inverter Generation 600V': 'inverter_generation_600v'
} as const;

/**
 * Normalize all feeder fields from database format to UI format
 * @param backendRow - Row from backend with snake_case field names
 * @returns Object with UI field names (Title Case)
 */
export const normalizeFeederFields = (backendRow: Record<string, any>): Record<string, any> => {
  const normalized: Record<string, any> = { ...backendRow };

  // Apply field name mappings
  Object.entries(feederFieldMapping).forEach(([dbName, uiName]) => {
    if (dbName === uiName) return; // Skip reverse mappings
    if (backendRow.hasOwnProperty(dbName)) {
      normalized[uiName] = backendRow[dbName];
    }
  });

  return normalized;
};

/**
 * Denormalize all feeder fields from UI format back to database format
 * @param uiRow - Row from UI with Title Case field names
 * @returns Object with database field names (snake_case)
 */
export const denormalizeFeederFields = (uiRow: Record<string, any>): Record<string, any> => {
  const denormalized: Record<string, any> = {};

  Object.entries(uiRow).forEach(([uiName, value]) => {
    const dbName = feederFieldMapping[uiName as keyof typeof feederFieldMapping] || uiName;
    denormalized[dbName] = value;
  });

  return denormalized;
};

// ==================== SAFE PARSER UTILITIES ====================
/**
 * Parse numeric value safely, returning null if invalid
 * @param value - Any value to parse as number
 * @returns Numeric value or null if invalid/empty
 */
export const parseNumberSafe = (value: any): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = parseFloat(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Convert value to string, handling null and undefined
 * @param value - Any value
 * @returns String representation or empty string if null/undefined
 */
export const toStringSafe = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  return String(value);
};

// ==================== BATCH NORMALIZATION ====================
/**
 * Normalize all fields in a row (all mapping functions applied)
 * @param backendRow - Row from backend with all DB field names
 * @returns Normalized row with consistent field naming
 */
export const normalizeAllFields = (backendRow: Record<string, any>): Record<string, any> => {
  let normalized = { ...backendRow };

  // Apply all normalizations
  normalized = normalizeFeederFields(normalized);
  if (normalized.net_import !== undefined) {
    normalized['Net Import'] = normalizeNetImport(normalized.net_import);
  }

  return normalized;
};

/**
 * Denormalize all fields in a row back to backend format
 * @param uiRow - Row from UI with all UI field names
 * @returns Denormalized row with DB field names
 */
export const denormalizeAllFields = (uiRow: Record<string, any>): Record<string, any> => {
  let denormalized = { ...uiRow };

  // Apply all denormalizations
  denormalized = denormalizeFeederFields(denormalized);
  if (denormalized['Net Import'] !== undefined) {
    denormalized['net_import'] = denormalizeNetImport(denormalized['Net Import']);
  }

  return denormalized;
};
